import type { CSSProperties } from 'react';
import { useLocation } from 'react-router';
import {
    PRODUCT_HERO_TRANSITION_NAME,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';

/**
 * Media-box style for a product tile, wherever it renders.
 *
 * The claim matches either end of the transition, so the tile is named both
 * on the way out (its own click) and on the way back (the listing
 * re-rendering under a Back from that product) — the morph is symmetric
 * without extra bookkeeping. Tiles for every other product stay unnamed.
 *
 * A page that renders the same product link twice names only the first of
 * them: a duplicate name makes the browser skip the transition outright, so
 * the claim registry hands it to one tile and leaves the rest unnamed.
 */
export const useProductTileHeroStyle = (
    to: string | undefined,
): CSSProperties | undefined => useTileHeroViewTransitionStyle(to);

/**
 * Hero style for the product detail gallery's first slide — the tile's
 * counterpart. Matching on this page's own path means the slide is named for
 * transitions arriving here and for transitions leaving here, and stays
 * unnamed for navigations that have nothing to do with this product.
 */
export const useProductDetailHeroStyle = (): CSSProperties | undefined => {
    const { pathname } = useLocation();
    return useTileHeroViewTransitionStyle(
        pathname,
        PRODUCT_HERO_TRANSITION_NAME,
    );
};
